import React from "react";
import { Form, Button, Alert, Modal, Dropdown } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { LuX } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import Editor from "components/controls/editor";
import Tooltip from "../../../controls/common/tooltip";
import ThreadView from "../common/threadView";
import Composer from "../common/composer";
import WidgetBase from "../common/widgetBase";
import Alias from "components/controls/alias";
import EnvelopeControls from "../common/envelopeControls";
import { Converter } from "../../workspace/converter";
import { canMutateSession } from "../../session/mutationGate";
import { seedFileUrl } from "../common/fileBlocks";
import Navigation from "context/nav";
import Utils from "context/utils";
import { withPreferences } from "context/preferences";
import "./index.css";

const PAGE_LIMIT = 10;
const THREAD_DRAIN_MAX_PAGES = 1000;

// Exported (named) for unit tests that inspect the raw class directly; the
// default export below is the withPreferences-wrapped component used by the app.
export class ThreadNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      posting: false,
      currentUserId: null,
      postError: null,
      // Composer metadata attached to the next send.
      composerMetadata: null,            // parsed object or null
      composerMetadataModalVisible: false,
      composerMetadataDraft: "",         // textarea contents in the modal
      composerMetadataError: null,
    };

    // The read-only view: mounted below, and the handle through which a post shows itself before
    // the server has it.
    this.view = React.createRef();
    this.nodeRef = React.createRef();
    this.pendingTempIds = new Set();
    this.authorPromises = {};
    this.boundAlias = null;
    this.boundCallback = null;
    this.boundSessionId = null;
    this.mounted = false;
  }

  /**
   * Lifecycle
   */

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;

    const api = this.getApi();
    if ( api && api.getCurrentUserId ) {
      api.getCurrentUserId().then( (id) => {
        if ( !this.mounted ) return;
        this.setState({ currentUserId: id });
      }, (_error) => {
        // Anonymous — leave currentUserId as null
      });
    }

  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    this.checkSyncResume();
  }

  /**
   * When sync flips OFF -> ON, actively re-tail rather than trusting the event channel.
   *
   * The catch-up dispatches `session_thread_item_posted` events, but the widget cannot rely on them
   * alone: the baseline it would fetch from is the user's OWN latest post, so items written by other
   * actors before it would be missed, and the channel itself has edges — a toggle racing an
   * outstanding POST, polling pausing after 180s. Asking the view to resync covers both: it fetches
   * strictly past what it holds, or loads the thread when it holds nothing to fetch past.
   */
  checkSyncResume() {
    const currentSync = !!(this.context && this.context.sessionSync);
    const prevSync = this._lastSyncState;
    this._lastSyncState = currentSync;

    if ( prevSync !== false || currentSync !== true ) return;
    if ( this.getEnvelopeState().status !== "ready" ) return;
    if ( this.view.current ) this.view.current.resync();
  }

  /**
   * What identifies the thread this widget is showing — the view reloads when it changes.
   *
   * The same three facts the reload gate always compared: which alias, in which session, and whether
   * the envelope is ready to be read at all. Firing a fetch against an envelope that is still
   * loading, missing or of the wrong type produces an `invalid_thread_envelope` error and a second,
   * confusing warning in the body — so "not ready" is its own key, and the view holds nothing.
   */
  sourceKey() {
    const ready = this.getEnvelopeState().status === "ready";
    return `${this.getBoundAlias() || ""}|${this.getSessionId() || ""}|${ready ? "ready" : "wait"}`;
  }

  /**
   * Where the view's bytes come from. Reads only — posting stays here, on top of it.
   */
  threadSource() {
    // READINESS FIRST, and the ids only after it. This runs during render now, where the old reload
    // gate ran only once the envelope was settled — so reading a revision off a shell that has not
    // got one yet is no longer a thing that cannot happen.
    const ready = this.getEnvelopeState().status === "ready";
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    if ( !ready || !alias || !sessionId ) {
      // Nothing to read: the body is showing a banner about why, and a fetch here would add a
      // second one contradicting it.
      return { key: this.sourceKey() };
    }
    const revisionId = this.getRevisionId();
    const api = this.getApi();
    if ( !revisionId ) return { key: this.sourceKey() };
    return {
      key: this.sourceKey(),
      listOlder: ({ cursor }) => {
        const options = { ascending: false, limit: PAGE_LIMIT };
        if ( cursor ) options.cursor = cursor;
        return api.threadItemsList(revisionId, sessionId, alias, options).then((response) => {
          const data = (response && response.data) || {};
          // Newest-first on the wire, oldest-first on screen.
          return { items: (data["items"] || []).slice().reverse(), cursor: data.cursor || null };
        });
      },
      listNewer: ({ since }) => this.drainNewer(revisionId, sessionId, alias, since),
      watch: (callback) => {
        this.boundCallback = callback;
        this.boundAlias = alias;
        if ( this.context ) this.context.addThreadEventListener(alias, callback);
      },
      unwatch: (callback) => {
        if ( this.boundAlias && this.context ) {
          this.context.removeThreadEventListener(this.boundAlias, callback);
        }
        this.boundAlias = null;
        this.boundCallback = null;
      },
      authorName: (userId) => this.resolveAuthorName(userId),
      authorProfilePath: (userId) => this.resolveProfilePath(userId)
    };
  }

  /**
   * Where a `file` content block's bytes come from — the same {api, revisionId, sessionId} that
   * threadSource() reads the thread itself with, and gated the same way: a shellInterface that has
   * not yet got a revision or a session (getApi()/getRevisionId() throw on one that hasn't) must
   * not be asked for one just because a file block happens to be on screen.
   */
  fileContext() {
    if ( this.getEnvelopeState().status !== "ready" ) return undefined;
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    if ( !alias || !sessionId ) return undefined;
    const revisionId = this.getRevisionId();
    if ( !revisionId ) return undefined;
    return { api: this.getApi(), revisionId, sessionId };
  }

  /**
   * Everything strictly newer than `since`, following the cursor across pages.
   *
   * Paged because a tail can be a burst larger than one page — a sync-resume backlog is the case
   * that made it necessary. Bounded so a cursor that never clears cannot spin forever.
   */
  async drainNewer(revisionId, sessionId, alias, since) {
    const api = this.getApi();
    const collected = [];
    const seen = new Set();
    let cursor = null;
    for ( let page = 0; page < THREAD_DRAIN_MAX_PAGES; page++ ) {
      const options = { ascending: true, createdSince: since, limit: PAGE_LIMIT };
      if ( cursor ) options.cursor = cursor;
      const response = await api.threadItemsList(revisionId, sessionId, alias, options);
      if ( !this.mounted ) return [];
      const raw = (response.data && response.data["items"]) || [];
      for ( const item of raw ) {
        if ( item && !seen.has(item.id) ) {
          seen.add(item.id);
          collected.push(item);
        }
      }
      cursor = (response.data && response.data.cursor) || null;
      if ( !cursor ) break;
    }
    return collected;
  }

  componentWillUnmount() {
    this.mounted = false;
    super.componentWillUnmount();
  }
  /**
   * Accessors
   */

  getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  getRevisionId() {
    return this.props.data.shellInterface.getRevision()["id"];
  }

  getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "thread");
  }

  // See WidgetBase.objectEnvelopeState for the states and why the list is never asked.
  getEnvelopeState() {
    return this.objectEnvelopeState(this.getBoundAlias(), "thread", "Loading thread...");
  }
  /**
   * Posting
   */

  // The placement's half of "can commit" — the composer owns the other half (text, chips) and ANDs this
  // in as the composer's `gate`, so the Send button, commit() and Enter all obey it. NOT a security boundary:
  // handleCommit() re-checks the session gate, since the failed-item retry bypasses the composer entirely.
  canSend() {
    const si = this.props.data.shellInterface;
    return !!(
      !this.state.posting
      && this.state.currentUserId
      && this.getEnvelopeState().status === "ready"
      && canMutateSession(si)
    );
  }

  // Tooltip copy for the composer Send button. The composer only renders while mutations are possible
  // (see showComposer in renderContent), but a disabled Send still has more than one cause: an empty draft,
  // a post in flight, or the account still loading (`currentUserId` unresolved). The copy is the same for all
  // of them. The gate behind canSend() also stops Enter, so a blocked state is not only a greyed button.
  sendTooltipText() {
    return "Send (Shift+Enter for newline)";
  }

  // The one choke point for a thread post, so the session-mutation gate is checked here; false tells the
  // composer to keep the draft. `posting` is checked after the upload pass: a second Send may upload its
  // files before being refused, which is harmless.
  handleCommit = (content) => {
    if ( !canMutateSession(this.props.data.shellInterface) ) return false;
    if ( !content || 0 === content.length || this.state.posting ) return false;
    // Nothing is cleared unless the post goes out: false keeps the draft and the attached metadata.
    let revisionId = null;
    try { revisionId = this.getRevisionId(); } catch (e) { revisionId = null; }
    if ( !this.getBoundAlias() || !this.getSessionId() || !revisionId ) return false;
    const metadata = this.state.composerMetadata;
    this.setState({ composerMetadata: null });
    this.postContent(content, metadata || undefined);
    return true;
  };

  // Where a staged file's bytes go: the bound thread's own namespace. Seeding the resolved-URL cache is
  // what lets the block this post carries render from memory instead of fetching what this tab just sent.
  uploadFile = ({ alias, name, contentType, bytes, file }) => {
    const revisionId = this.getRevisionId();
    const sessionId = this.getSessionId();
    return this.getApi()
      .sessionFileUpload(revisionId, sessionId, alias, name, contentType, bytes)
      .then(() => { seedFileUrl(revisionId, sessionId, alias, file); });
  };

  // The optimistic post half — tempId, appendLocal, threadItemPost, settleLocal/failLocal — shared by
  // handleCommit() (freshly built content) and retryFailedItem() (a failed item's original content and
  // metadata, verbatim). Resolves its own alias/session/revision so either caller can invoke it with
  // just the payload; owns `posting` from here onward, exactly as the pre-upload tail always did.
  postContent(content, metadata) {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const tempId = Utils.generateUniqueId();
    this.pendingTempIds.add(tempId);

    const optimisticItem = {
      id: tempId,
      __tempId: tempId,
      __pending: true,
      user_id: this.state.currentUserId,
      content,
      created_at: Utils.currentTimeUtcIso()
    };
    if ( metadata ) {
      optimisticItem.metadata = metadata;
    }

    if ( this.view.current ) this.view.current.appendLocal(optimisticItem);
    this.setState({ posting: true, postError: null });

    const api = this.getApi();
    // Only forward metadata if the user actually attached some — the
    // backend's schema drops the field when undefined.
    const parentId = undefined;
    api.threadItemPost(revisionId, sessionId, alias, content, parentId, metadata || undefined)
      .then( (response) => {
        if ( !this.mounted ) return;
        const serverItem = response.data && response.data["item"];
        if ( !serverItem ) {
          // No item in the response — the row stays, marked failed, so it can be retried.
          if ( this.view.current ) this.view.current.failLocal(tempId);
          this.setState({
            posting: false,
            postError: "Server did not return a thread item."
          });
          this.pendingTempIds.delete(tempId);
          return;
        }

        // The view settles it: replaced in place normally, and — because a post races the tail it
        // will arrive on — left alone if a reload already brought the server item in, or appended if
        // that reload wiped the optimistic row without picking the item up.
        if ( this.view.current ) this.view.current.settleLocal(tempId, serverItem);
        this.setState({ posting: false, postError: null });
        this.pendingTempIds.delete(tempId);
      }, (error) => {
        if ( !this.mounted ) return;
        if ( this.view.current ) this.view.current.failLocal(tempId);
        this.setState({
          posting: false,
          postError: error && error.message ? error.message : "Failed to post thread item."
        });
        // Keep tempId in pendingTempIds so the user can retry.
      });
  }

  /**
   * Composer metadata attachment
   */

  openComposerMetadataModal = () => {
    const draft = this.state.composerMetadata
      ? JSON.stringify(this.state.composerMetadata, null, 2)
      : "{\n  \n}";
    this.setState({
      composerMetadataModalVisible: true,
      composerMetadataDraft: draft,
      composerMetadataError: null
    });
  }

  closeComposerMetadataModal = () => {
    this.setState({
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  applyComposerMetadata = () => {
    const text = (this.state.composerMetadataDraft || "").trim();
    if ( text === "" ) {
      // Empty metadata is treated as "no attachment".
      this.setState({
        composerMetadata: null,
        composerMetadataModalVisible: false,
        composerMetadataError: null
      });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      this.setState({ composerMetadataError: "Invalid JSON: " + e.message });
      return;
    }
    if ( typeof parsed !== "object" || parsed === null || Array.isArray(parsed) ) {
      this.setState({ composerMetadataError: "Metadata must be a JSON object." });
      return;
    }
    this.setState({
      composerMetadata: parsed,
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  clearComposerMetadata = () => {
    this.setState({ composerMetadata: null });
  }

  /**
   * Item expansion (long content + metadata)
   */



  retryFailedItem(item) {
    if ( !item || !item.__failed ) return;
    // Posting a thread item is a session-data mutation. retryFailedItem() calls postContent()
    // directly rather than routing through handleCommit(), so the gate must be re-checked here — mirrors
    // the choke-point comment on handleCommit() itself.
    if ( !canMutateSession(this.props.data.shellInterface) ) return;
    // Drop the failed row, then re-post: a retry is a new post, and leaving the old one would show
    // the same message twice once it lands. Content and metadata go through verbatim — reducing to
    // `content[0].text` would drop everything past the first block on a multi-segment or attachment
    // post.
    if ( this.view.current ) this.view.current.removeLocal(item.__tempId);
    this.pendingTempIds.delete(item.__tempId);
    this.postContent(item.content, item.metadata || null);
  }
  /**
   * Author name resolution (cached)
   */

  resolveAuthorName(userId) {
    if ( !userId ) return "unknown";
    const api = this.getApi();
    const cachedResponse = api.usersCacheById ? api.usersCacheById[userId] : null;
    if ( cachedResponse ) {
      const user = (cachedResponse.data && cachedResponse.data["user"]) || {};
      return user["name"] || user["alias"] || user["email"] || userId.slice(-5);
    }
    if ( !this.authorPromises[userId] ) {
      this.authorPromises[userId] = api.usersIdGet(userId).then(() => {
        if ( this.mounted ) this.forceUpdate();
      }, () => {
        delete this.authorPromises[userId];
      });
    }
    return userId.slice(-5);
  }

  /**
   * Rendering
   */

  getClassName() {
    return "playground-thread-node";
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <Alias value={alias} variant="chip" />;
  }

  renderHeaderActions() {
    const envelope = this.getEnvelopeState();
    const value = envelope.status === "ready" ? envelope.value : null;
    return (
      <EnvelopeControls
        alias={this.getBoundAlias()}
        value={value}
        shellInterface={this.props.data.shellInterface}
        sessionManager={this.context}
        refreshTooltip="Refresh thread"
        // Reloading the envelope value alone doesn't refresh the messages —
        // those come from threadItemsList. Ask the view to resync so a
        // refresh re-fetches the thread items too.
        onRefresh={() => { if ( this.view.current ) this.view.current.resync(); }}
      />
    );
  }



  resolveProfilePath(userId) {
    if ( !userId ) return null;
    // Prefer the user's alias for a cleaner URL, falling back to the id.
    const api = this.getApi();
    const cached = api && api.usersCacheById ? api.usersCacheById[userId] : null;
    const user = cached && cached.data ? cached.data.user || cached.data : null;
    const slug = (user && user.alias) || userId;
    return Navigation.userProfilePath(slug);
  }

  // The composer's `chipsExtra`: metadata attached to the next send. It wears the staged-file chip's own
  // class because it is the same kind of thing — something attached, with a way to take it off.
  renderMetadataChip() {
    return (
      <button type="button" className="playground-thread-composer-attached"
        onClick={this.openComposerMetadataModal}>
        <CgDetailsMore className="playground-thread-composer-attached-icon" />
        <span className="playground-thread-composer-attached-label">metadata</span>
        <Tooltip text="Remove">
          <span role="button" tabIndex={0} className="playground-thread-composer-attached-remove"
            onClick={(e) => { e.stopPropagation(); this.clearComposerMetadata(); }}
            onKeyDown={(e) => {
              if ( "Enter" === e.key || " " === e.key ) {
                e.preventDefault();
                e.stopPropagation();
                this.clearComposerMetadata();
              }
            }}>
            <LuX />
          </span>
        </Tooltip>
      </button>
    );
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");
    const envelopeState = this.getEnvelopeState();
    const isReady = envelopeState.status === "ready";

    // One rule for every mutation surface in the playground: it renders only while mutations are
    // actually possible. canMutateSession() collapses ALL the reasons they might not be — no write
    // access, a committed revision, a closed session — so the composer behaves the same way whichever
    // one applies, instead of vanishing for one and lingering disabled for another. Why mutations are
    // blocked isn't surfaced anywhere — the control is simply absent.
    const showComposer = canMutateSession(this.props.data.shellInterface) && isReady;

    return (
      <div ref={this.nodeRef} className="playground-thread-node" style={{ width, height, maxHeight }}>
        {envelopeState.status === "unbound" &&
          this.renderNotice({ variant: "info", message: 'Connect a value to the "thread" port.' })
        }
        {envelopeState.status === "no-session" &&
          this.renderNotice({ variant: "info", message: "Select a session to view this widget." })
        }
        {envelopeState.status === "loading" &&
          this.renderLoading(envelopeState.reason || "Loading…")
        }
        {envelopeState.status === "missing" &&
          this.renderNotice({
            variant: "info",
            message: `"${envelopeState.alias}" is not available in the selected session yet.`,
          })
        }
        {envelopeState.status === "error" &&
          this.renderNotice({ variant: "warning", message: `Could not load "${envelopeState.alias}".` })
        }
        {envelopeState.status === "wrong-type" &&
          this.renderNotice({
            variant: "warning",
            message: `The "thread" port expects a "thread" value, but "${envelopeState.alias}" is "${envelopeState.type}".`,
          })
        }
        {/* THE VIEW (common/threadView.js): loading, paging, the live tail, scroll behaviour and
            the item rendering. Mission Control's cell screen mounts the same one for a run's
            transcript, so a change to how a thread READS reaches both without being made twice.
            It is read-only, and posting sits on top of it — the composer below, and the optimistic
            row handed to it through the handles it exposes for exactly that. */}
        {isReady &&
          <ThreadView
            ref={this.view}
            source={this.threadSource()}
            contentView={contentView}
            currentUserId={this.state.currentUserId}
            timezone={this.props.preferences && this.props.preferences["app.timezone"]}
            emptyMessage="No messages yet. Type something below to start the thread."
            onRetry={(item) => this.retryFailedItem(item)}
            fileContext={this.fileContext()}
          />
        }

        {showComposer &&
          <div className="playground-thread-composer">
            {this.state.postError &&
              <Alert variant="warning" className="playground-thread-composer-error">
                {this.state.postError}
              </Alert>
            }
            <Form onSubmit={(event) => event.preventDefault()}>
              <Composer
                fileBase={this.getBoundAlias()}
                upload={this.uploadFile}
                // THIS WIDGET'S OWN CAP, in pixels, because that is what it has always been: main sized
                // this field with a hard-coded `20 * 6` and the real line-height is 22.5px, so the shared
                // six-row cap would silently grow a shipped field from 120px to ~145px. The shared
                // default is right for the panels it was written for; this one states its own.
                maxHeight={120}
                onCommit={this.handleCommit}
                gate={() => this.canSend()}
                onError={(message) => this.setState({ postError: message })}
                placeholder="Type a message..."
                textareaClassName="nodrag nowheel"
                commit={({ ready, commit }) => (
                  // The span carries the handlers: a native disabled <button> dispatches no mouse events. As a
                  // flex item it needs no styling and cannot shrink below the 32px Button (composerAlign.html).
                  <Tooltip text={this.sendTooltipText()}>
                    <span>
                      <Button className="bh-composer-send" disabled={!ready} onClick={commit}>
                        <VscSend />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                menuExtra={
                  <Dropdown.Item onClick={this.openComposerMetadataModal}>
                    <CgDetailsMore />&nbsp;&nbsp;
                    {this.state.composerMetadata ? "Edit Metadata" : "Add Metadata"}
                  </Dropdown.Item>
                }
                chipsExtra={this.state.composerMetadata ? this.renderMetadataChip() : null}
              />
            </Form>
          </div>
        }

        {this.renderMetadataModal()}
      </div>
    );
  }

  renderMetadataModal() {
    if ( !this.state.composerMetadataModalVisible ) return null;
    return (
      <Modal
        show={this.state.composerMetadataModalVisible}
        onHide={this.closeComposerMetadataModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Attach Metadata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Editor
            name="playground-thread-composer-metadata-editor"
            width="100%"
            mode="json"
            wrapEnabled="true"
            value={this.state.composerMetadataDraft}
            onChange={(value) => this.setState({
              composerMetadataDraft: value, composerMetadataError: null
            })}
            minLines={5}
            maxLines={24}
            setOptions={{ printMarginColumn: 120 }}
          />
          <div style={{ marginTop: "10px", color: "var(--bh-text-muted-2)", fontSize: "13px" }}>
            Metadata attached to the next sent message. Must be a JSON object.
            Leave empty to send without metadata.
          </div>
          {this.state.composerMetadataError &&
            <Alert variant="warning" style={{ marginTop: "10px", marginBottom: 0 }}>
              {this.state.composerMetadataError}
            </Alert>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.closeComposerMetadataModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={this.applyComposerMetadata}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withPreferences(
  ThreadNode,
  ["app.timezone"],
  // Resolve preferences with the same org/blob scope the playground shell
  // uses, so blob- or org-level timezone overrides are honored. Without
  // this, withPreferences falls back to the user-level scope only and any
  // blob-scoped timezone is silently ignored.
  props => props.data.shellInterface.getScope()
);
